import { MenuItem } from '../data/menuItems'

// Define which paths belong to which modules
const DENTAL_CLINIC_PATHS = [
  '/dental',
  '/patients',
  '/appointments',
  '/invoices/dental'
]

const ISP_PATHS = [
  '/subscriptions'
]

const PRODUCTION_PATHS = [
  '/production',
  '/sorting'
]

const RESTAURANT_PATHS = [
  '/restaurant',
  '/restaurant/pos',
  '/restaurant/menu-setup',
  '/restaurant/delivery',
  '/restaurant/orders-queue',
  '/restaurant/kitchen-management'
]

const CAR_RENTAL_PATHS = [
  '/car-rental'
]

const FIXED_ASSETS_PATHS = [
  '/fixed-assets'
]

const FUEL_STATION_PATHS = [
  '/fuel-station',
  '/fuel-meter'
]

export function isDentalClinicPath(path: string): boolean {
  return DENTAL_CLINIC_PATHS.some(modulePath =>
    path.toLowerCase().startsWith(modulePath.toLowerCase())
  )
}

export function isISPPath(path: string): boolean {
  return ISP_PATHS.some(modulePath =>
    path.toLowerCase().startsWith(modulePath.toLowerCase())
  )
}

export function isProductionPath(path: string): boolean {
  return PRODUCTION_PATHS.some(modulePath =>
    path.toLowerCase().startsWith(modulePath.toLowerCase())
  )
}

export function isRestaurantPath(path: string): boolean {
  return RESTAURANT_PATHS.some(modulePath =>
    path.toLowerCase().startsWith(modulePath.toLowerCase())
  )
}

export function isCarRentalPath(path: string): boolean {
  return CAR_RENTAL_PATHS.some(modulePath =>
    path.toLowerCase().startsWith(modulePath.toLowerCase())
  )
}

export function isFixedAssetsPath(path: string): boolean {
  return FIXED_ASSETS_PATHS.some(modulePath =>
    path.toLowerCase().startsWith(modulePath.toLowerCase())
  )
}

export function isFuelStationPath(path: string): boolean {
  if (!path) return false
  const normalized = path.split('?')[0].toLowerCase()
  return FUEL_STATION_PATHS.some(modulePath =>
    normalized === modulePath || normalized.startsWith(`${modulePath}/`) || normalized.startsWith(modulePath)
  )
}

export function isFuelAclMenu(menu: {
  menuURL?: string
  MenuURL?: string
  menuName?: string
  MenuName?: string
  menuText?: string
  MenuText?: string
}): boolean {
  const url = menu.menuURL || menu.MenuURL || ''
  if (url && isFuelStationPath(url)) return true
  const name = menu.menuName || menu.MenuName || ''
  if (/^Fuel/i.test(name)) return true
  const text = `${menu.menuText || ''} ${menu.MenuText || ''}`
  return text.includes('محطات المحروقات')
}

export function parseCompanyModuleFlag(
  data: Record<string, unknown> | null | undefined,
  camel: string,
  pascal: string,
  fallback: boolean
): boolean {
  if (!data) return fallback
  const value = data[camel] ?? data[pascal]
  if (value === undefined || value === null) return fallback
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }
  return Boolean(value)
}

export type ModuleFlags = {
  enableDentalClinicModule: boolean
  enableISPModule: boolean
  enableProductionModule: boolean
  enableRestaurantModule: boolean
  enableCarRentalModule: boolean
  enableFixedAssetsModule: boolean
  enableFuelStationModule: boolean
}

function isFuelStationMenuGroup(item: MenuItem): boolean {
  const ar = item.label?.ar || ''
  const en = (item.label?.en || '').toLowerCase()
  return ar.includes('محطات المحروقات') || en.includes('fuel station')
}

export function isModuleAllowed(path: string, modules: ModuleFlags): boolean {
  if (isDentalClinicPath(path) && !modules.enableDentalClinicModule) {
    return false
  }
  if (isISPPath(path) && !modules.enableISPModule) {
    return false
  }
  if (isProductionPath(path) && !modules.enableProductionModule) {
    return false
  }
  if (isRestaurantPath(path) && !modules.enableRestaurantModule) {
    return false
  }
  if (isCarRentalPath(path) && !modules.enableCarRentalModule) {
    return false
  }
  if (isFixedAssetsPath(path) && !modules.enableFixedAssetsModule) {
    return false
  }
  if (isFuelStationPath(path) && !modules.enableFuelStationModule) {
    return false
  }
  return true
}

export function filterMenuItems(items: MenuItem[], modules: ModuleFlags): MenuItem[] {
  return items
    .map(item => {
      if (!modules.enableFuelStationModule && isFuelStationMenuGroup(item)) {
        return null
      }

      if (item.path && !isModuleAllowed(item.path, modules)) {
        return null
      }

      if (item.children) {
        const filteredChildren = filterMenuItems(item.children, modules)
        if (filteredChildren.length === 0) {
          return null
        }
        return {
          ...item,
          children: filteredChildren
        }
      }

      return item
    })
    .filter(Boolean) as MenuItem[]
}
